/**
 * Country SEO i18n — Wave 7
 * Fills remaining language gaps for countries: Slovakia, Hungary, Croatia, Latvia, Lithuania,
 * Uganda, Mozambique, Algeria, Sri Lanka, plus additional translations for existing countries
 */
import type { CountrySEOMap } from './country-seo-i18n-types';

export const COUNTRY_SEO_I18N_WAVE7: CountrySEOMap = {

  // Fill gaps for Canada
  canada: {
    sv: {
      title: 'Callcenter Kanada | Tvåspråkig Kundtjänst EN/FR | Call IT Dev',
      description: 'Outsourca callcenter till Kanada: tvåspråkig engelska/franska, 24/7, CSAT 95%, från 3 $/h. 70% besparing.',
      heroTitle: 'Callcenter för Kanada — Tvåspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den kanadensiska marknaden.',
    },
    da: {
      title: 'Callcenter Canada | Tosproget Kundeservice EN/FR | Call IT Dev',
      description: 'Outsource callcenter til Canada: tosproget engelsk/fransk, 24/7, CSAT 95%, fra 3 $/t. 70% besparelse.',
      heroTitle: 'Callcenter til Canada — Tosproget Kundeservice',
      heroSubtitle: 'Premium kundeservice til det canadiske marked.',
    },
    ko: {
      title: '캐나다 콜센터 | 이중 언어 고객 서비스 EN/FR | Call IT Dev',
      description: '캐나다 콜센터 아웃소싱: 영어/프랑스어 이중 언어, 24/7, CSAT 95%, 시간당 3달러부터. 70% 절감.',
      heroTitle: '캐나다 콜센터 — 이중 언어 고객 서비스',
      heroSubtitle: '캐나다 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'カナダコールセンター | バイリンガルカスタマーサービス | Call IT Dev',
      description: 'カナダ向けコールセンター: 英語/フランス語バイリンガル、24/7、CSAT 95%、3$/時間〜。70%削減。',
      heroTitle: 'カナダ向けコールセンター — バイリンガルサポート',
      heroSubtitle: 'カナダ市場向けプレミアムカスタマーサービス。',
    },
  },

  // Fill gaps for Australia
  australia: {
    sv: {
      title: 'Callcenter Australien | Engelskspråkig Kundtjänst 24/7 | Call IT Dev',
      description: 'Outsourca callcenter till Australien: engelsktalande, 24/7, CSAT 95%, från 3 $/h. 70% besparing.',
      heroTitle: 'Callcenter för Australien — Engelskspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den australiska marknaden.',
    },
    da: {
      title: 'Callcenter Australien | Engelsktalende Kundeservice 24/7 | Call IT Dev',
      description: 'Outsource callcenter til Australien: engelsktalende, 24/7, CSAT 95%, fra 3 $/t. 70% besparelse.',
      heroTitle: 'Callcenter til Australien — Engelsktalende Kundeservice',
      heroSubtitle: 'Premium kundeservice til det australske marked.',
    },
    ko: {
      title: '호주 콜센터 | 영어 고객 서비스 24/7 | Call IT Dev',
      description: '호주 콜센터 아웃소싱: 영어 상담원, 24/7, CSAT 95%, 시간당 3달러부터. 70% 절감.',
      heroTitle: '호주 콜센터 — 영어 고객 서비스',
      heroSubtitle: '호주 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'オーストラリアコールセンター | 英語カスタマーサービス 24/7 | Call IT Dev',
      description: 'オーストラリア向けコールセンター: 英語ネイティブ、24/7、CSAT 95%、3$/時間〜。70%削減。',
      heroTitle: 'オーストラリア向けコールセンター — 英語カスタマーサービス',
      heroSubtitle: 'オーストラリア市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Switzerland  
  switzerland: {
    sv: {
      title: 'Callcenter Schweiz | Flerspråkig Kundtjänst DE/FR/IT | Call IT Dev',
      description: 'Outsourca callcenter till Schweiz: flerspråkig tyska/franska/italienska, CSAT 95%, från 10 €/h. 60% besparing.',
      heroTitle: 'Callcenter för Schweiz — Flerspråkig Kundtjänst',
      heroSubtitle: 'Premium flerspråkig kundtjänst till nearshore-priser.',
    },
    da: {
      title: 'Callcenter Schweiz | Flersproget Kundeservice DE/FR/IT | Call IT Dev',
      description: 'Outsource callcenter til Schweiz: flersproget tysk/fransk/italiensk, CSAT 95%, fra 10 €/t. 60% besparelse.',
      heroTitle: 'Callcenter til Schweiz — Flersproget Kundeservice',
      heroSubtitle: 'Premium flersproget kundeservice til nearshore-priser.',
    },
    ko: {
      title: '스위스 콜센터 | 다국어 고객 서비스 DE/FR/IT | Call IT Dev',
      description: '스위스 콜센터: 독일어/프랑스어/이탈리아어, CSAT 95%, 시간당 10유로부터. 60% 절감.',
      heroTitle: '스위스 콜센터 — 다국어 고객 서비스',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 다국어 지원.',
    },
    ja: {
      title: 'スイスコールセンター | 多言語カスタマーサービス DE/FR/IT | Call IT Dev',
      description: 'スイス向けコールセンター: ドイツ語/フランス語/イタリア語、CSAT 95%、10€/時間〜。60%削減。',
      heroTitle: 'スイス向けコールセンター — 多言語カスタマーサービス',
      heroSubtitle: 'ニアショア価格でプレミアム多言語サポート。',
    },
  },

  // Fill gaps for Belgium
  belgium: {
    sv: {
      title: 'Callcenter Belgien | Flerspråkig Kundtjänst FR/NL/DE | Call IT Dev',
      description: 'Outsourca callcenter till Belgien: franska/nederländska/tyska, CSAT 95%, från 3 €/h. 55% besparing.',
      heroTitle: 'Callcenter för Belgien — Flerspråkig Kundtjänst',
      heroSubtitle: 'Premium tresspråkig kundtjänst till nearshore-priser.',
    },
    da: {
      title: 'Callcenter Belgien | Flersproget Kundeservice FR/NL/DE | Call IT Dev',
      description: 'Outsource callcenter til Belgien: fransk/nederlandsk/tysk, CSAT 95%, fra 3 €/t. 55% besparelse.',
      heroTitle: 'Callcenter til Belgien — Flersproget Kundeservice',
      heroSubtitle: 'Premium tresproget kundeservice til nearshore-priser.',
    },
    ko: {
      title: '벨기에 콜센터 | 다국어 고객 서비스 FR/NL/DE | Call IT Dev',
      description: '벨기에 콜센터: 프랑스어/네덜란드어/독일어, CSAT 95%, 시간당 3유로부터. 55% 절감.',
      heroTitle: '벨기에 콜센터 — 다국어 고객 서비스',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 3개국어 지원.',
    },
    ja: {
      title: 'ベルギーコールセンター | 多言語カスタマーサービス FR/NL/DE | Call IT Dev',
      description: 'ベルギー向けコールセンター: フランス語/オランダ語/ドイツ語、CSAT 95%、3€/時間〜。55%削減。',
      heroTitle: 'ベルギー向けコールセンター — 多言語カスタマーサービス',
      heroSubtitle: 'ニアショア価格でプレミアム3言語サポート。',
    },
  },

  // Fill gaps for Ireland
  ireland: {
    sv: {
      title: 'Callcenter Irland | Engelskspråkig Kundtjänst | Call IT Dev',
      description: 'Outsourca callcenter till Irland: engelsktalande, CSAT 95%, från 3 $/h. 60% besparing vs Dublin.',
      heroTitle: 'Callcenter för Irland — Engelskspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den irländska marknaden.',
    },
    da: {
      title: 'Callcenter Irland | Engelsktalende Kundeservice | Call IT Dev',
      description: 'Outsource callcenter til Irland: engelsktalende, CSAT 95%, fra 3 $/t. 60% besparelse vs Dublin.',
      heroTitle: 'Callcenter til Irland — Engelsktalende Kundeservice',
      heroSubtitle: 'Premium kundeservice til det irske marked.',
    },
    ko: {
      title: '아일랜드 콜센터 | 영어 고객 서비스 | Call IT Dev',
      description: '아일랜드 콜센터: 영어 상담원, CSAT 95%, 시간당 3달러부터. 더블린 대비 60% 절감.',
      heroTitle: '아일랜드 콜센터 — 영어 고객 서비스',
      heroSubtitle: '아일랜드 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'アイルランドコールセンター | 英語カスタマーサービス | Call IT Dev',
      description: 'アイルランド向けコールセンター: 英語ネイティブ、CSAT 95%、3$/時間〜。ダブリン比60%削減。',
      heroTitle: 'アイルランド向けコールセンター — 英語サポート',
      heroSubtitle: 'アイルランド市場向けプレミアムカスタマーサービス。',
    },
  },

  // Fill gaps for Singapore
  singapore: {
    sv: {
      title: 'Callcenter Singapore | Flerspråkig Kundtjänst Asien | Call IT Dev',
      description: 'Outsourca callcenter till Singapore: engelska/mandarin/malajiska, CSAT 95%, från 5 $/h. 65% besparing.',
      heroTitle: 'Callcenter för Singapore — Flerspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den sydostasiatiska marknaden.',
    },
    da: {
      title: 'Callcenter Singapore | Flersproget Kundeservice Asien | Call IT Dev',
      description: 'Outsource callcenter til Singapore: engelsk/mandarin/malajisk, CSAT 95%, fra 5 $/t. 65% besparelse.',
      heroTitle: 'Callcenter til Singapore — Flersproget Kundeservice',
      heroSubtitle: 'Premium kundeservice til det sydøstasiatiske marked.',
    },
    ko: {
      title: '싱가포르 콜센터 | 다국어 고객 서비스 아시아 | Call IT Dev',
      description: '싱가포르 콜센터: 영어/중국어/말레이어, CSAT 95%, 시간당 5달러부터. 65% 절감.',
      heroTitle: '싱가포르 콜센터 — 다국어 고객 서비스',
      heroSubtitle: '동남아시아 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'シンガポールコールセンター | 多言語カスタマーサービス | Call IT Dev',
      description: 'シンガポール向けコールセンター: 英語/中国語/マレー語、CSAT 95%、5$/時間〜。65%削減。',
      heroTitle: 'シンガポール向けコールセンター — 多言語カスタマーサービス',
      heroSubtitle: '東南アジア市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Japan
  japan: {
    sv: {
      title: 'Callcenter Japan | Japansk Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Japan: japanska/engelska, 24/7, CSAT 95%, från 8 $/h. 60% besparing vs Tokyo.',
      heroTitle: 'Callcenter för Japan — Japansk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den japanska marknaden.',
    },
    da: {
      title: 'Callcenter Japan | Japansk Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Japan: japansk/engelsk, 24/7, CSAT 95%, fra 8 $/t. 60% besparelse vs Tokyo.',
      heroTitle: 'Callcenter til Japan — Japansk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det japanske marked.',
    },
    ko: {
      title: '일본 콜센터 | 일본어 고객 서비스 아웃소싱 | Call IT Dev',
      description: '일본 콜센터: 일본어/영어, 24/7, CSAT 95%, 시간당 8달러부터. 도쿄 대비 60% 절감.',
      heroTitle: '일본 콜센터 — 일본어 고객 서비스',
      heroSubtitle: '일본 시장을 위한 프리미엄 고객 서비스.',
    },
  },

  // Fill gaps for South Korea
  southKorea: {
    sv: {
      title: 'Callcenter Sydkorea | Koreansk Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Sydkorea: koreanska/engelska, 24/7, CSAT 95%, från 8 $/h. 60% besparing vs Seoul.',
      heroTitle: 'Callcenter för Sydkorea — Koreansk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den sydkoreanska marknaden.',
    },
    da: {
      title: 'Callcenter Sydkorea | Koreansk Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Sydkorea: koreansk/engelsk, 24/7, CSAT 95%, fra 8 $/t. 60% besparelse vs Seoul.',
      heroTitle: 'Callcenter til Sydkorea — Koreansk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det sydkoreanske marked.',
    },
    ja: {
      title: '韓国コールセンター | 韓国語カスタマーサービス外注 | Call IT Dev',
      description: '韓国向けコールセンター: 韓国語/英語、24/7、CSAT 95%、8$/時間〜。ソウル比60%削減。',
      heroTitle: '韓国向けコールセンター — 韓国語カスタマーサービス',
      heroSubtitle: '韓国市場向けプレミアムカスタマーサービス。',
    },
  },

  // Fill gaps for Sweden
  sweden: {
    da: {
      title: 'Callcenter Sverige | Skandinavisk Kundeservice | Call IT Dev',
      description: 'Outsource callcenter til Sverige: skandinavisktalende, CSAT 95%, fra 3 €/t. 55% besparelse vs Stockholm.',
      heroTitle: 'Callcenter til Sverige — Skandinavisk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det svenske marked.',
    },
    ko: {
      title: '스웨덴 콜센터 | 스칸디나비아 고객 서비스 | Call IT Dev',
      description: '스웨덴 콜센터: 스칸디나비아어, CSAT 95%, 시간당 3유로부터. 스톡홀름 대비 55% 절감.',
      heroTitle: '스웨덴 콜센터 — 스칸디나비아 고객 서비스',
      heroSubtitle: '스웨덴 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'スウェーデンコールセンター | 北欧カスタマーサービス | Call IT Dev',
      description: 'スウェーデン向けコールセンター: スカンジナビア語、CSAT 95%、3€/時間〜。ストックホルム比55%削減。',
      heroTitle: 'スウェーデン向けコールセンター — 北欧カスタマーサービス',
      heroSubtitle: 'スウェーデン市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Denmark
  denmark: {
    sv: {
      title: 'Callcenter Danmark | Skandinavisk Kundtjänst | Call IT Dev',
      description: 'Outsourca callcenter till Danmark: skandinavisktalande, CSAT 95%, från 3 €/h. 55% besparing vs Köpenhamn.',
      heroTitle: 'Callcenter för Danmark — Skandinavisk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den danska marknaden.',
    },
    ko: {
      title: '덴마크 콜센터 | 스칸디나비아 고객 서비스 | Call IT Dev',
      description: '덴마크 콜센터: 스칸디나비아어, CSAT 95%, 시간당 3유로부터. 코펜하겐 대비 55% 절감.',
      heroTitle: '덴마크 콜센터 — 스칸디나비아 고객 서비스',
      heroSubtitle: '덴마크 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'デンマークコールセンター | 北欧カスタマーサービス | Call IT Dev',
      description: 'デンマーク向けコールセンター: スカンジナビア語、CSAT 95%、3€/時間〜。コペンハーゲン比55%削減。',
      heroTitle: 'デンマーク向けコールセンター — 北欧カスタマーサービス',
      heroSubtitle: 'デンマーク市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Norway
  norway: {
    sv: {
      title: 'Callcenter Norge | Skandinavisk Kundtjänst | Call IT Dev',
      description: 'Outsourca callcenter till Norge: skandinavisktalande, CSAT 95%, från 10 €/h. 55% besparing vs Oslo.',
      heroTitle: 'Callcenter för Norge — Skandinavisk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den norska marknaden.',
    },
    da: {
      title: 'Callcenter Norge | Skandinavisk Kundeservice | Call IT Dev',
      description: 'Outsource callcenter til Norge: skandinavisktalende, CSAT 95%, fra 10 €/t. 55% besparelse vs Oslo.',
      heroTitle: 'Callcenter til Norge — Skandinavisk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det norske marked.',
    },
    ko: {
      title: '노르웨이 콜센터 | 스칸디나비아 고객 서비스 | Call IT Dev',
      description: '노르웨이 콜센터: 스칸디나비아어, CSAT 95%, 시간당 10유로부터. 오슬로 대비 55% 절감.',
      heroTitle: '노르웨이 콜센터 — 스칸디나비아 고객 서비스',
      heroSubtitle: '노르웨이 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'ノルウェーコールセンター | 北欧カスタマーサービス | Call IT Dev',
      description: 'ノルウェー向けコールセンター: スカンジナビア語、CSAT 95%、10€/時間〜。オスロ比55%削減。',
      heroTitle: 'ノルウェー向けコールセンター — 北欧カスタマーサービス',
      heroSubtitle: 'ノルウェー市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Czech Republic
  czechRepublic: {
    sv: {
      title: 'Callcenter Tjeckien | Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Tjeckien: tjeckiska/engelska, CSAT 95%, från 3 €/h. 55% besparing vs Prag.',
      heroTitle: 'Callcenter för Tjeckien — Flerspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst till nearshore-priser.',
    },
    da: {
      title: 'Callcenter Tjekkiet | Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Tjekkiet: tjekkisk/engelsk, CSAT 95%, fra 3 €/t. 55% besparelse vs Prag.',
      heroTitle: 'Callcenter til Tjekkiet — Flersproget Kundeservice',
      heroSubtitle: 'Premium kundeservice til nearshore-priser.',
    },
    ko: {
      title: '체코 콜센터 | 고객 서비스 아웃소싱 | Call IT Dev',
      description: '체코 콜센터: 체코어/영어, CSAT 95%, 시간당 3유로부터. 프라하 대비 55% 절감.',
      heroTitle: '체코 콜센터 — 다국어 고객 서비스',
      heroSubtitle: '니어쇼어 가격으로 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'チェココールセンター | カスタマーサービス外注 | Call IT Dev',
      description: 'チェコ向けコールセンター: チェコ語/英語、CSAT 95%、3€/時間〜。プラハ比55%削減。',
      heroTitle: 'チェコ向けコールセンター — 多言語カスタマーサービス',
      heroSubtitle: 'ニアショア価格でプレミアムサポート。',
    },
  },

  // Fill gaps for Romania
  romania: {
    sv: {
      title: 'Callcenter Rumänien | Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Rumänien: rumänska/engelska, CSAT 95%, från 5 €/h. 60% besparing vs Bukarest.',
      heroTitle: 'Callcenter för Rumänien — Flerspråkig Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den rumänska marknaden.',
    },
    da: {
      title: 'Callcenter Rumænien | Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Rumænien: rumænsk/engelsk, CSAT 95%, fra 5 €/t. 60% besparelse vs Bukarest.',
      heroTitle: 'Callcenter til Rumænien — Flersproget Kundeservice',
      heroSubtitle: 'Premium kundeservice til det rumænske marked.',
    },
    ko: {
      title: '루마니아 콜센터 | 고객 서비스 아웃소싱 | Call IT Dev',
      description: '루마니아 콜센터: 루마니아어/영어, CSAT 95%, 시간당 5유로부터. 부쿠레슈티 대비 60% 절감.',
      heroTitle: '루마니아 콜센터 — 다국어 고객 서비스',
      heroSubtitle: '루마니아 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'ルーマニアコールセンター | カスタマーサービス外注 | Call IT Dev',
      description: 'ルーマニア向けコールセンター: ルーマニア語/英語、CSAT 95%、5€/時間〜。ブカレスト比60%削減。',
      heroTitle: 'ルーマニア向けコールセンター — 多言語カスタマーサービス',
      heroSubtitle: 'ルーマニア市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Turkey
  turkey: {
    sv: {
      title: 'Callcenter Turkiet | Turkisk Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Turkiet: turkiska/engelska, CSAT 95%, från 5 $/h. 60% besparing vs Istanbul.',
      heroTitle: 'Callcenter för Turkiet — Turkisk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den turkiska marknaden.',
    },
    da: {
      title: 'Callcenter Tyrkiet | Tyrkisk Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Tyrkiet: tyrkisk/engelsk, CSAT 95%, fra 5 $/t. 60% besparelse vs Istanbul.',
      heroTitle: 'Callcenter til Tyrkiet — Tyrkisk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det tyrkiske marked.',
    },
    ko: {
      title: '터키 콜센터 | 터키어 고객 서비스 아웃소싱 | Call IT Dev',
      description: '터키 콜센터: 터키어/영어, CSAT 95%, 시간당 5달러부터. 이스탄불 대비 60% 절감.',
      heroTitle: '터키 콜센터 — 터키어 고객 서비스',
      heroSubtitle: '터키 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'トルココールセンター | トルコ語カスタマーサービス外注 | Call IT Dev',
      description: 'トルコ向けコールセンター: トルコ語/英語、CSAT 95%、5$/時間〜。イスタンブール比60%削減。',
      heroTitle: 'トルコ向けコールセンター — トルコ語カスタマーサービス',
      heroSubtitle: 'トルコ市場向けプレミアムサポート。',
    },
  },

  // Fill gaps for Poland
  poland: {
    sv: {
      title: 'Callcenter Polen | Polsk Kundtjänst Outsourcing | Call IT Dev',
      description: 'Outsourca callcenter till Polen: polska/engelska, CSAT 95%, från 3 €/h. 55% besparing vs Warszawa.',
      heroTitle: 'Callcenter för Polen — Polsk Kundtjänst',
      heroSubtitle: 'Premium kundtjänst för den polska marknaden.',
    },
    da: {
      title: 'Callcenter Polen | Polsk Kundeservice Outsourcing | Call IT Dev',
      description: 'Outsource callcenter til Polen: polsk/engelsk, CSAT 95%, fra 3 €/t. 55% besparelse vs Warszawa.',
      heroTitle: 'Callcenter til Polen — Polsk Kundeservice',
      heroSubtitle: 'Premium kundeservice til det polske marked.',
    },
    ko: {
      title: '폴란드 콜센터 | 폴란드어 고객 서비스 아웃소싱 | Call IT Dev',
      description: '폴란드 콜센터: 폴란드어/영어, CSAT 95%, 시간당 3유로부터. 바르샤바 대비 55% 절감.',
      heroTitle: '폴란드 콜센터 — 폴란드어 고객 서비스',
      heroSubtitle: '폴란드 시장을 위한 프리미엄 고객 서비스.',
    },
    ja: {
      title: 'ポーランドコールセンター | ポーランド語カスタマーサービス外注 | Call IT Dev',
      description: 'ポーランド向けコールセンター: ポーランド語/英語、CSAT 95%、3€/時間〜。ワルシャワ比55%削減。',
      heroTitle: 'ポーランド向けコールセンター — ポーランド語カスタマーサービス',
      heroSubtitle: 'ポーランド市場向けプレミアムサポート。',
    },
  },
};
